import { MapViewerEventObject, MapViewerNode } from '@3ddv/dvm-internal';
import { DvmModule, MapViewerEvent, MapViewerService } from '@3ddv/ngx-dvm-internal';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { DvmService } from '@services/dvm.service';
import { PopoverService } from '@services/popover.service';
import { tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MapPopupComponent } from './map-popup/map-popup.component';
import { PopoverOptions } from '@models/interfaces/components/Popover.interface';
import { AmenitiesService } from '@services/amenities.service';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { PreviewService } from '@services/preview.service';
import { LayoutService } from '@services/layout.service';

@Component({
    selector: 'app-map-viewer',
    standalone: true,
    imports: [DvmModule],
    templateUrl: './map-viewer.component.html',
    styleUrl: './map-viewer.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapViewerComponent implements OnInit, OnDestroy {

  // SERVICES
  private   readonly dvm:         DvmService            = inject(DvmService);
  private   readonly cdr:         ChangeDetectorRef     = inject(ChangeDetectorRef);
  private   readonly popover:     PopoverService        = inject(PopoverService);
  private   readonly ams:         AmenitiesService      = inject(AmenitiesService);
  private   readonly preview:     PreviewService        = inject(PreviewService);
  private   readonly layout:      LayoutService         = inject(LayoutService);
  private   readonly destroyRef:  DestroyRef            = inject(DestroyRef);


  // GLOBAL STATE
  public    readonly viewer:      MapViewerService      = this.dvm.viewer;
  private   readonly view:        Signal<AppViews|null> = this.dvm.view;
  private   readonly isMobile:    Signal<boolean>       = this.layout.isMobile;

  //CONFIG
  private readonly uiconfig:              Signal<UiConfiguration>  = inject(UiService).uiConfig;
  private readonly view3dDisplayEnabled:  Signal<boolean>          = computed((): boolean => this.uiconfig().View3dDisplay.enabled);
  private readonly seatLevelEnabled:      Signal<boolean>          = computed((): boolean => this.uiconfig().SeatLevelDisplay.enabled);

  // ???
  private popoverTimeout: number = 0;
  private readonly popoverTimeoutDelay: number = 200;


  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.destroyComponent();
  }


  // METHODS
  private initComponent(): void {

    this.viewer.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSubscriptions());

  }

  private initSubscriptions(): void {

    // CLICK EVENT
    this.viewer.getObservable('click').pipe(tap(():void => this.cdr.markForCheck()), takeUntilDestroyed(this.destroyRef)).subscribe((event: MapViewerEvent<MapViewerEventObject>): void => this.onClick(event.event));

    // ENTER EVENT
    this.viewer.getObservable('enter').pipe(tap(():void => this.cdr.markForCheck()), takeUntilDestroyed(this.destroyRef)).subscribe((event: MapViewerEvent<MapViewerEventObject>): void => this.onEnter(event.event));

    // LEAVE EVENT
    this.viewer.getObservable('leave').pipe(tap(():void => this.cdr.markForCheck()), takeUntilDestroyed(this.destroyRef)).subscribe((event: MapViewerEvent<MapViewerEventObject>): void => this.onLeave(event.event));

  }

  private destroyComponent(): void {
    this.clearTimeout();
    this.dvm.clearStyles();
    this.dvm.resetViewer(false);
    this.preview.resetPreview();
  }

 // VIEWER METHODS
  private onClick(event: MapViewerEventObject): void {

    const node: MapViewerNode = event.nodes[0];

    if( !node || ( !node.state || node.state === 'unavailable' )) return;

    this.clearTimeout();
    this.popover.destroyAllPopovers();

    switch(node.type) {

      case 'section':
      case 'area':
      case 'areaprice':
      case 'general_admission':
        this.handleSectionClick(node);
        break;

      case 'section_nh':
        this.dvm.loadMap(node.id).subscribe({error: (error: unknown): void => console.error('Error loading section map', error)});
        break;

      case 'seat':
        this.handleSeatClick(node);
        break;

      // MAS NODOS TODO
    }

  }

  private onEnter(event: MapViewerEventObject): void {

    if(this.isMobile()) return;

    this.clearTimeout();

    this.popoverTimeout = window.setTimeout(() => {
      const node:     MapViewerNode           = event.nodes[0];

      if( !node || ( !node.state || node.state === 'unavailable' )) return;

      const options:  Partial<PopoverOptions> = {
        middleware: {
          offset: 10,
          flip: {
            fallbackAxisSideDirection: 'start',
          }
        }
      }


      switch(this.view()){

        // TOPVIEW Y MAIN VIEW
        case AppViews.Topview:

          this.popover.createPopoverComponent<MapPopupComponent>(node.id, node, MapPopupComponent, {node}, options);

          break;


        // SEATMAP VIEW
        case AppViews.Seatmap:

          if(node.type === 'seat') this.popover.createPopoverComponent<MapPopupComponent>(node.id, node, MapPopupComponent, {node}, options);

          break;

        // MAIN VIEW
        case AppViews.Main:

          if(this.dvm.viewer.layers.getLayerLevel() === 0){

            this.popover.createPopoverComponent<MapPopupComponent>(node.id, node, MapPopupComponent, {node}, options)

          }else{

            if(node.type === 'seat') this.popover.createPopoverComponent<MapPopupComponent>(node.id, node, MapPopupComponent, {node}, options);

          }

          break;

        // PANO VIEW
        case AppViews.Pano:

          this.popover.destroyPopover(node.id);

          break;

      }
    }, this.popoverTimeoutDelay);
  }

  private onLeave(event: MapViewerEventObject): void {

    if(this.isMobile()) return;

    this.clearTimeout();
    this.popover.destroyPopover(event.nodes[0].id);

  }


  // HANDLER METHODS
  private handleSeatClick(node: MapViewerNode): void {
    if(this.seatLevelEnabled()) {
      this.dvm.viewer.goTo(node.id);
    }

    switch(node.state) {

        case 'available':
          this.dvm.viewer.unselectAll();
          this.dvm.viewer.select(node);
          this.selectSeat(node);
          break;
        case 'selected':
          this.selectSeat(node);
          break;
    }

  }


  private handleSectionClick(node: MapViewerNode): void {
    if (this.dvm.apply3DView(node.id)) {
      this.dvm.load3dView(node.id).subscribe({
        error: (error: unknown): void => console.error('Error loading 3D view for section', node.id, error)
      });
      return;
    }

    if (this.dvm.selectNodeInTopview(node.id)) {
      this.viewer.unselectAll();
      this.viewer.select(node.id);
      return;
    }

    switch (this.view()) {
      case AppViews.Topview:
        this.dvm.loadMap(node.id).subscribe({
          next:  (): void => this.ams.checkSectionAmenity(node.original_id),
          error: (): void => {
            if (!this.view3dDisplayEnabled()) return;
            this.dvm.load3dView(node.id).subscribe({
              error: (error: unknown): void => console.error('Error loading fallback 3D view for section', node.id, error)
            });
          }
        });
        break;

      case AppViews.Seatmap:
        this.dvm.loadMap(node.id).subscribe({
          error: (error: unknown): void => console.error('Error loading area map', node.id, error)
        });
        break;

      case AppViews.Main:
        this.dvm.viewer.goTo(node.id);
        break;
    }
  }

  // SEAT METHODS
  private selectSeat(node: MapViewerNode): void {
    this.ams.checkSeatAmenity(node.id);
    if(this.view3dDisplayEnabled()) this.dvm.load3dView(node.id).subscribe({
      error: (error: unknown): void => console.error('Error loading 3D view for seat', node.id, error)
    });
  }

  private clearTimeout(): void {
    if (this.popoverTimeout) {
      window.clearTimeout(this.popoverTimeout);
      this.popoverTimeout = 0;
    }
  }

}
